import { allCountries } from 'country-region-data';

// MTM (Miss To Mrs) Easy Checkout flow — standalone v3 build.
// Ported from the Claude Design prototype (project ce2652ad-8d4f-433d-8a08-f3f4262f007b,
// ClickUp 869f98bh0). Business rules below were all read directly off the
// design's own Component class source (script-tag dump / Logic.toString() on
// the live render_preview), not guessed, and re-verified against screenshots.

export const COMMERCE = {
  // Letter suffix on the plan code → cadence. 'a' bills every 14 DAYS, not
  // "0.5 months" — read verbatim off the design's FREQ_BY_LETTER. `tail` is
  // the trailing clause the renewal bullet appends after "Renews at $X ...".
  frequencyByLetter: {
    a: { name: 'Every 2 Weeks', short: 'Accelerated', tight: 'Accelerated', days: 14 },
    s: { name: 'Monthly', short: 'Monthly', tight: 'Monthly', months: 1, tail: 'of each month' },
    e: { name: 'Every 2 Months', short: 'Every 2 months', tight: 'Every 2 mo', months: 2, tail: 'every 2 months' },
    q: { name: 'Every 3 Months', short: 'Every 3 months', tight: 'Every 3 mo', months: 3, tail: 'every 3 months' },
  },

  shippingAddons: { US: 'us-shipping', CA: 'ca-shipping' },
  installmentCode: 'installment-4',
  promoPrefix: 'promo-',
  promoSuffix: 'off',
  countries: ['US', 'CA'],

  // Real production Braintree/Google credentials — not new secrets, already
  // live in MTM's legacy /checkout (checkout.page.jsx) and in
  // mtm-react-app's /subscribe-v2 route.
  wallets: {
    displayName: 'Miss To Mrs Box',
    country: 'US',
    currency: 'USD',
    locale: 'en_US',
    googleMerchantId: 'BCR2DN4TWKILTWKN',
    braintree: {
      paypal: 'production_8h2d68qv_4d8x87xpxqk6pvm6',
      applepay: 'production_8h2d68qv_4d8x87xpxqk6pvm6',
      venmo: 'production_gpsqmbch_4d8x87xpxqk6pvm6',
    },
  },
};

// Flat first-box price per promo code — ported verbatim from the design
// (STEP 2 OF 4 and STEP 4 OF 4 — same table, kept in sync there on purpose).
export const PROMO_PRICES = { mtm30off: 29.00, mtm50off: 21.00 };
export const DEFAULT_RENEWAL_PRICE = 41.99;

// Bonus pill copy + struck-through value, ported from the design (STEP 2 OF 4).
export const BONUS_TITLES = { namechange: 'Name Change Kit', freebridesweatshirt: 'BRIDE Sweatshirt' };
export const BONUS_VALUES = { namechange: 40, freebridesweatshirt: 59 };

// The 16 plans the static Steps 1-2 can hand over as ?pln= — the only values
// the checkout accepts. A plan code is "mtm-{months}mo-{letter}": months is the
// minimum commitment, and the letter IS the cadence (a/s/e/q, see
// frequencyByLetter above). Which plan a wedding date gets is the static
// page's job; this app only checks the code it is given against this list.
export const PLANS = {
  'mtm-3mo-s': { months: 3, letter: 's' },
  'mtm-4mo-a': { months: 4, letter: 'a' },
  'mtm-4mo-s': { months: 4, letter: 's' },
  'mtm-5mo-a': { months: 5, letter: 'a' },
  'mtm-5mo-s': { months: 5, letter: 's' },
  'mtm-6mo-s': { months: 6, letter: 's' },
  'mtm-7mo-s': { months: 7, letter: 's' },
  'mtm-7mo-e': { months: 7, letter: 'e' },
  'mtm-8mo-a': { months: 8, letter: 'a' },
  'mtm-8mo-s': { months: 8, letter: 's' },
  'mtm-8mo-e': { months: 8, letter: 'e' },
  'mtm-9mo-s': { months: 9, letter: 's' },
  'mtm-10mo-a': { months: 10, letter: 'a' },
  'mtm-10mo-s': { months: 10, letter: 's' },
  'mtm-10mo-e': { months: 10, letter: 'e' },
  'mtm-10mo-q': { months: 10, letter: 'q' },
};
export const VALID_PLAN_CODES = Object.keys(PLANS);

const planOf = (code) => PLANS[(code || '').trim().toLowerCase()];

// Sweatshirt eligibility — the design's own rule (STEP 4's sweatshirtBlocked,
// build_hints.md › Step 1 › "Bonus rule"): the BRIDE Sweatshirt is only for
// weddings 5+ months away, so it is swapped for the Name Change Kit on every
// plan in the under-134-day rows: mtm-3mo-*, mtm-4mo-*, mtm-5mo-* and mtm-8mo-a
// (which only appears as the alternative to 4mo-s / 5mo-s). Judged on the plan
// being bought (the ?pln handed over), not on the date.
export function sweatshirtBlocked(planCode, bonusCode) {
  if (bonusCode !== 'freebridesweatshirt') return false;
  const plan = planOf(planCode);
  return !!plan && (plan.months <= 5 || (plan.months === 8 && plan.letter === 'a'));
}

// ?bonus= is free text (URL or sessionStorage), but step 4 turns whatever lands
// in state into a "promo-<code>" Recurly add-on, and Recurly rejects the whole
// purchase for an item it doesn't have ("Add-on is invalid ... Item must
// exist"). So only codes with a real add-on behind them get through — a typo
// like "freesweatshirt" is dropped, same as react-app's checkout.page.jsx
// dropping codes that aren't in recurly_get_addon_list.
export function bonusCodeFor(planCode, rawBonusCode) {
  const code = sweatshirtBlocked(planCode, rawBonusCode) ? 'namechange' : rawBonusCode;
  return Object.keys(BONUS_TITLES).includes(code) ? code : '';
}

// ?promo= has the same exposure as ?bonus=: step 4 sends it to Recurly as the
// coupon AND as a "promo-<code minus 'off'>" add-on, so a code with no add-on
// behind it fails the purchase. The flow can only price the codes in
// PROMO_PRICES (anything else would show the full first-box price while Recurly
// charged less), so those are the only ones accepted. Matched ignoring case,
// but the code is returned as given so what Recurly receives doesn't change.
export function promoCodeFor(rawPromo) {
  const code = rawPromo || '';
  return Object.keys(PROMO_PRICES).includes(code.toLowerCase()) ? code : '';
}

export const REQUIRED_FOR_DELIVERY = ['first', 'last', 'street', 'city', 'region', 'postal', 'email'];

export const POSTAL_PATTERNS = {
  US: /^\d{5}(-\d{4})?$/,
  CA: /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/,
};

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const US_NON_STATE_CODES = ['AS', 'FM', 'GU', 'MH', 'MP', 'PW', 'PR', 'VI', 'AA', 'AE', 'AP'];

export function countryOptions(codes = COMMERCE.countries) {
  return allCountries
    .filter((c) => codes.includes(c[1]))
    .map((c) => ({ value: c[1], label: c[0], flag: c[1].toLowerCase() }));
}

export function regionOptions(countryCode) {
  const row = allCountries.find((c) => c[1] === countryCode);
  if (!row) return [];
  const regions = countryCode === 'US'
    ? row[2].filter((r) => !US_NON_STATE_CODES.includes(r[1]))
    : row[2];
  return regions.map((r) => ({ value: r[1], label: r[0] }));
}

// Where a customer with no date/plan is sent to start over. Steps 1-2 are a
// separate static page now, and this is the funnel's front door on the real
// site (every homepage button points at /subscribe/).
// DEPLOY ORDER: /subscribe/ must already serve that static Step 1. While it
// still redirects to /subscribe-v2/ (this app), "Start over" would loop.
export const START_URL = 'https://misstomrsbox.com/subscribe/';

// Cadence name + months from a plan code's trailing letter.
export function cadenceFromPlanCode(planCode) {
  const plan = planOf(planCode);
  return COMMERCE.frequencyByLetter[plan ? plan.letter : 's'];
}

// "Got questions? Tap to explore" (design, Steps 3 and 4): the same chips and
// answers on both steps. "Gift receipts" is added for a gift order only.
export const LOOKING_FOR = {
  title: 'Got questions? Tap to explore',
  labels: [
    'First box shipping', "What's in the box", 'One time box', 'Other plans', 'Number of boxes',
    'When it ends', 'Prepaid options', 'Cancellation policy', 'Reviews',
  ],
  giftLabels: ['Gift receipts'],
  otherLabel: 'Ask your question',
  otherPlaceholder: 'What would you like to know?',
  otherSend: 'Send',
  thanksTitle: 'Thanks, noted',
  thanksBody: "We'll use this to make the page clearer.",
  answers: {
    'First box shipping': 'The first box ships fast \u2014 usually within 1\u20132 business days after your order is placed. Most customers receive it within 5\u20137 days, depending on location and carrier timing.',
    "What's in the box": 'Each Miss To Mrs Box includes 6\u20138 full-size products curated for the bride\u2019s engagement timeline.\n\nBoxes can include planning tools, bride apparel, self-care products, beauty accessories, party essentials, keepsakes, and honeymoon/newlywed favorites.\n\nThe exact contents are a surprise, but every box is designed to help the bride feel more prepared, celebrated, and excited for what\u2019s next.',
    'One time box': 'A one-time box option isn\u2019t available yet. Our current plans deliver a series of themed boxes throughout the wedding planning journey.',
    'Other plans': "We make it simple: choose delivery frequency only, with the option to pause or cancel anytime if you're not satisfied. Your subscription automatically ends after the final themed box.",
    'Number of boxes': 'Number of boxes depends on the wedding date - up to 12 different themed boxes celebrating the journey from Miss to Mrs. Pause or cancel subscription anytime.',
    'When it ends': 'Automatically, after your final themed box \u2014 no need to cancel. You can still pause or cancel anytime from your account.',
    'Prepaid options': "Prepaid plans aren't offered at checkout right now.",
    'Cancellation policy': 'Pause or cancel anytime from the account portal. Promotional first-box price and other promos require three boxes minimum. Cancelling before the 3rd box will incur a $25 early cancellation fee.',
    'Reviews': 'Over 12,000 reviews on Trustpilot with 4.6 rating. Our company has shipped 1,000,000+ boxes and is loved by 100,000+ brides-to-be and gift-senders.',
    'Gift receipts': 'Boxes never contain prices, receipts or invoices \u2014 those go to the account email only.',
  },
};
